import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { apiGet, type Service, type ServiceHealth } from "../api";
import { Icons } from "../components/icons";

interface HeatmapDay {
  date: string;
  granularity?: "day" | "hour";
  worst_status: string;
  incidents_count: number;
}

/** Rango temporal del heatmap. "hour" = N celdas de 1h. "day" = N celdas de 1d. */
type HeatmapRange = { granularity: "day" | "hour"; n: number };

interface MetricsPoint {
  bucket_start: string;
  latency_p50_ms: number | null;
  latency_p95_ms: number | null;
  requests: number;
  errors: number;
}

interface MetricsResponse {
  service_id: string;
  range: string;
  points: MetricsPoint[];
}

interface RecentError {
  service_id: string;
  action: string;
  code: string | null;
  message: string | null;
  count: number;
  time: string;
  http_status: number | null;
  sample_trace_id: string | null;
}

const STATUS_LABEL: Record<string, string> = {
  ok: "Operativo",
  degraded: "Degradado",
  partial: "Interrupción parcial",
  major: "Caída mayor",
  maintenance: "Mantenimiento",
  unknown: "Sin datos",
};

export default function ServiceDetail() {
  // El path param se llama :slug por compat histórica, pero ahora puede ser
  // un uuid (lo que linkea la grilla) o un slug (URLs bookmarkeadas). El
  // backend acepta ambos.
  const { slug: identifier } = useParams();

  const [service, setService] = useState<Service | null>(null);
  const [health, setHealth] = useState<ServiceHealth | null>(null);
  const [heatmap, setHeatmap] = useState<HeatmapDay[]>([]);
  const [metrics, setMetrics] = useState<MetricsResponse | null>(null);
  const [errors, setErrors] = useState<RecentError[]>([]);
  // Rango del heatmap. Si granularidad es "hour", cada celda = 1 hora.
  // Si es "day", cada celda = 1 día. El backend acepta `?hours=24` o `?days=N`.
  const [heatmapRange, setHeatmapRange] = useState<HeatmapRange>({
    granularity: "day",
    n: 90,
  });

  useEffect(() => {
    if (!identifier) return;
    apiGet<Service>(`/v1/services/${identifier}`).then(setService).catch(console.error);
    apiGet<ServiceHealth>(`/v1/services/${identifier}/health`).then(setHealth).catch(console.error);
    apiGet<MetricsResponse>(`/v1/services/${identifier}/metrics?range=24h&buckets=48`).then(setMetrics).catch(console.error);
    apiGet<RecentError[]>(`/v1/services/${identifier}/errors/recent?limit=8`).then(setErrors).catch(console.error);

    const interval = setInterval(() => {
      apiGet<ServiceHealth>(`/v1/services/${identifier}/health`).then(setHealth).catch(() => {});
      apiGet<MetricsResponse>(`/v1/services/${identifier}/metrics?range=24h&buckets=48`).then(setMetrics).catch(() => {});
    }, 15_000);
    return () => clearInterval(interval);
  }, [identifier]);

  // Refetch del heatmap cuando cambia el rango (en un useEffect aparte para
  // que cambiar el toggle no dispare los otros fetches).
  useEffect(() => {
    if (!identifier) return;
    const qs =
      heatmapRange.granularity === "hour"
        ? `hours=${heatmapRange.n}`
        : `days=${heatmapRange.n}`;
    apiGet<HeatmapDay[]>(`/v1/services/${identifier}/heatmap?${qs}`)
      .then(setHeatmap)
      .catch(console.error);
  }, [identifier, heatmapRange]);

  const uptimePct = useMemo(() => {
    if (heatmap.length === 0) return 100;
    const okDays = heatmap.filter((d) => d.worst_status === "ok").length;
    return (okDays / heatmap.length) * 100;
  }, [heatmap]);

  if (!service) {
    return (
      <div className="status-shell">
        <p className="text-muted">Cargando…</p>
      </div>
    );
  }

  const status = health?.status ?? "unknown";

  return (
    <div className="status-shell">
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--fg-3)" }}>
        <Link to="/services" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          ← Servicios
        </Link>
        <span style={{ color: "var(--fg-4)" }}>/</span>
        <span className="text-mono">{service.slug}</span>
      </div>

      {/* Hero status */}
      <div className="status-hero">
        <div className="icon">
          <Icons.Activity size={20} />
        </div>
        <div style={{ flex: 1 }}>
          <h2>{service.name}</h2>
          <div className="meta">
            {service.category} · {health?.requests_24h ?? 0} requests / 24h ·
            {" "}error rate {health?.error_rate_24h != null ? `${(health.error_rate_24h * 100).toFixed(2)}%` : "—"}
          </div>
        </div>
        <span className={`badge badge-${status}`} style={{ fontSize: 12, padding: "5px 12px" }}>
          <span className="dot" style={{ background: "currentColor" }} />
          {STATUS_LABEL[status]}
        </span>
      </div>

      {/* Uptime heatmap con selector de rango */}
      <section className="card">
        <header
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 14,
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontFamily: "var(--font-display)",
                fontSize: 16,
                fontWeight: 700,
                letterSpacing: "-0.01em",
              }}
            >
              Uptime · últim{heatmapRange.granularity === "hour" ? "as" : "os"} {heatmapRange.n}{" "}
              {heatmapRange.granularity === "hour" ? "horas" : "días"}
            </h3>
            <div className="text-sm text-muted" style={{ marginTop: 4 }}>
              {heatmapRange.granularity === "hour"
                ? "Cada barra es una hora. Verde = operativo, amarillo/naranja/rojo = degradado / parcial / mayor."
                : "Cada barra es un día. Verde = operativo, amarillo/naranja/rojo = degradado / parcial / mayor."}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <RangeToggle current={heatmapRange} onChange={setHeatmapRange} />
            <div
              style={{
                fontFamily: "var(--font-display)",
                fontWeight: 700,
                fontSize: 24,
                letterSpacing: "-0.02em",
                color:
                  uptimePct > 99
                    ? "var(--st-ok)"
                    : uptimePct > 95
                    ? "var(--st-degraded)"
                    : "var(--st-major)",
                minWidth: 90,
                textAlign: "right",
              }}
            >
              {uptimePct.toFixed(2)}%
            </div>
          </div>
        </header>

        {heatmap.length === 0 ? (
          <div className="empty" style={{ padding: 30 }}>
            <div className="text-muted">Sin datos históricos todavía.</div>
          </div>
        ) : (
          <>
            <div className="uptime-90">
              {heatmap.map((d) => {
                // Cuando es granularidad por hora, el `date` viene como ISO con
                // timestamp. Lo formateamos en local time para el tooltip.
                const label =
                  heatmapRange.granularity === "hour"
                    ? new Date(d.date).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" })
                    : d.date;
                return (
                  <div
                    key={d.date}
                    className={`day ${d.worst_status === "ok" ? "ok" : d.worst_status}`}
                    title={`${label} — ${STATUS_LABEL[d.worst_status] ?? d.worst_status}${d.incidents_count > 0 ? ` · ${d.incidents_count} incidentes` : ""}`}
                  />
                );
              })}
            </div>
            <div className="uptime-legend">
              <span>
                hace {heatmapRange.n}{" "}
                {heatmapRange.granularity === "hour" ? "h" : "días"}
              </span>
              <span style={{ display: "flex", gap: 12 }}>
                <Legend color="var(--st-ok)" label="ok" />
                <Legend color="var(--st-degraded)" label="degraded" />
                <Legend color="var(--st-partial)" label="partial" />
                <Legend color="var(--st-major)" label="major" />
              </span>
              <span>{heatmapRange.granularity === "hour" ? "ahora" : "hoy"}</span>
            </div>
          </>
        )}
      </section>

      {/* Metrics */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
        <MetricCard
          label="p50 (24h)"
          value={health?.latency_p50_ms != null ? `${health.latency_p50_ms} ms` : "—"}
        />
        <MetricCard
          label="p95 (24h)"
          value={
            health?.latency_p95_ms != null
              ? health.latency_p95_ms < 1000
                ? `${health.latency_p95_ms} ms`
                : `${(health.latency_p95_ms / 1000).toFixed(1)} s`
              : "—"
          }
          danger={(health?.latency_p95_ms ?? 0) > 3000}
        />
        <MetricCard
          label="Error rate 24h"
          value={health?.error_rate_24h != null ? `${(health.error_rate_24h * 100).toFixed(1)}%` : "—"}
          danger={(health?.error_rate_24h ?? 0) > 0.1}
        />
      </div>

      {/* Latency sparkline + requests bars */}
      {metrics && metrics.points.length > 0 && (
        <section className="card">
          <header style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
            <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700 }}>
              Latencia p95 · últimas 24h
            </h3>
            <span className="text-xs text-muted text-mono">{metrics.points.length} buckets</span>
          </header>
          <LatencyChart points={metrics.points} />
        </section>
      )}

      {/* Endpoints / actions breakdown (recent errors as proxy) */}
      <section className="card">
        <header style={{ marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700 }}>
            Errores recientes
          </h3>
          <div className="text-sm text-muted" style={{ marginTop: 4 }}>
            Últimos errores agrupados por endpoint y código de respuesta · 24h
          </div>
        </header>

        {errors.length === 0 ? (
          <div className="empty" style={{ padding: 30 }}>
            <div className="ico"><Icons.Check size={20} /></div>
            <div>Sin errores en las últimas 24 horas.</div>
          </div>
        ) : (
          <div>
            <div
              className="endpoint-row"
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                color: "var(--fg-3)",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              <div>Endpoint</div>
              <div className="num">HTTP</div>
              <div className="num">Código</div>
              <div className="num">Count</div>
              <div className="num">Último</div>
            </div>
            {errors.map((e, i) => (
              <div key={i} className="endpoint-row">
                <div>
                  <div className="name">{e.action}</div>
                  {e.message && (
                    <div className="text-xs text-muted" style={{ marginTop: 2 }}>
                      {e.message.slice(0, 70)}{e.message.length > 70 ? "…" : ""}
                    </div>
                  )}
                </div>
                <div className="num">
                  <span className="badge badge-major" style={{ fontSize: 11 }}>
                    {e.http_status ?? "—"}
                  </span>
                </div>
                <div className="num text-mono text-muted">{e.code ?? "—"}</div>
                <div className="num" style={{ fontWeight: 700, fontFamily: "var(--font-display)", color: "var(--st-major)" }}>
                  {e.count}
                </div>
                <div className="num text-mono text-muted">
                  {e.time ? new Date(e.time).toLocaleTimeString("es-AR", { hour12: false }) : "—"}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Link a traces — uuid en vez de slug: el backend `maybe_resolve_slug`
          devuelve nil para slugs cuando org_id=nil (admin global) y los traces
          quedan vacíos. El uuid resuelve siempre. */}
      <Link to={`/traces?service_ids=${encodeURIComponent(service.id)}`} className="btn btn-secondary" style={{ alignSelf: "flex-start" }}>
        Ver traces de este servicio →
      </Link>
    </div>
  );
}

// Toggle de rango temporal del heatmap. Soporta granularidad horaria (24h —
// 24 celdas de 1 hora) y diaria (7d / 30d / 90d — N celdas de 1 día).
// El backend acepta `?hours=N` o `?days=N` según corresponda.
const RANGE_OPTIONS: Array<{ key: string; range: HeatmapRange; label: string }> = [
  { key: "24h", range: { granularity: "hour", n: 24 }, label: "24h" },
  { key: "7d",  range: { granularity: "day",  n: 7  }, label: "7d"  },
  { key: "30d", range: { granularity: "day",  n: 30 }, label: "30d" },
  { key: "90d", range: { granularity: "day",  n: 90 }, label: "90d" },
];

function rangeKey(r: HeatmapRange): string {
  return r.granularity === "hour" ? `${r.n}h` : `${r.n}d`;
}

function RangeToggle({
  current,
  onChange,
}: {
  current: HeatmapRange;
  onChange: (range: HeatmapRange) => void;
}) {
  const currentKey = rangeKey(current);
  return (
    <div
      style={{
        display: "inline-flex",
        gap: 2,
        padding: 3,
        background: "var(--bg-2)",
        borderRadius: 8,
        border: "1px solid var(--border-1)",
      }}
    >
      {RANGE_OPTIONS.map((opt) => {
        const active = currentKey === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => onChange(opt.range)}
            style={{
              padding: "5px 12px",
              background: active ? "var(--bg-0)" : "transparent",
              border: "none",
              borderRadius: 5,
              cursor: "pointer",
              fontSize: 12,
              fontWeight: active ? 700 : 500,
              fontFamily: "var(--font-mono)",
              color: active ? "var(--fg-1)" : "var(--fg-3)",
              boxShadow: active ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
              transition: "background 0.15s, color 0.15s",
            }}
            onMouseEnter={(e) => {
              if (active) return;
              (e.currentTarget as HTMLButtonElement).style.color = "var(--fg-1)";
            }}
            onMouseLeave={(e) => {
              if (active) return;
              (e.currentTarget as HTMLButtonElement).style.color = "var(--fg-3)";
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <span style={{ width: 8, height: 8, borderRadius: 2, background: color }} />
      {label}
    </span>
  );
}

function MetricCard({ label, value, danger }: { label: string; value: string; danger?: boolean }) {
  return (
    <div className="card" style={{ padding: 18 }}>
      <div className="text-xs" style={{ color: "var(--fg-3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {label}
      </div>
      <div
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: 28,
          letterSpacing: "-0.02em",
          marginTop: 6,
          color: danger ? "var(--st-major)" : "var(--fg-1)",
        }}
      >
        {value}
      </div>
    </div>
  );
}

function LatencyChart({ points }: { points: MetricsPoint[] }) {
  const values = points.map((p) => p.latency_p95_ms ?? 0);
  const max = Math.max(1, ...values);
  const w = 800;
  const h = 100;
  const stepX = w / Math.max(values.length - 1, 1);
  const path =
    points
      .map((p, i) => {
        const x = i * stepX;
        const y = h - ((p.latency_p95_ms ?? 0) / max) * (h - 8) - 4;
        return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
      })
      .join(" ") || "";

  const fillPath = path ? `${path} L ${w} ${h} L 0 ${h} Z` : "";

  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: "100%", height: 100, display: "block" }}>
      <defs>
        <linearGradient id="latency-gradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.4" />
          <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
        </linearGradient>
      </defs>
      {fillPath && <path d={fillPath} fill="url(#latency-gradient)" />}
      {path && <path d={path} fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />}
    </svg>
  );
}
